import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { ArrowRight, Clock } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { api, type TrialUsageStatus } from '../../lib/api'

function formatDate(value?: string | null) {
  if (!value) return 'the configured deadline'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'the configured deadline'
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

function deadlineMessage(value?: string | null) {
  const fallback =
    'The Optioneer free plan is ending on the configured deadline. Upgrade to Premium to keep using your account after this date.'
  if (!value) return fallback

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const startOfDeadline = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const daysUntilDeadline = Math.round(
    (startOfDeadline.getTime() - startOfToday.getTime()) / 86_400_000
  )
  const dateLabel = formatDate(value)

  if (daysUntilDeadline === 0) {
    return `The Optioneer free plan ends today, ${dateLabel}. Upgrade to Premium to keep using your account after today.`
  }

  if (daysUntilDeadline === 1) {
    return `The Optioneer free plan ends tomorrow, ${dateLabel}. Upgrade to Premium to keep using your account after tomorrow.`
  }

  return `The Optioneer free plan is ending on ${dateLabel}. Upgrade to Premium to keep using your account after this date.`
}

export default function LegacyFreePlanBanner() {
  const { user } = useAuth()
  const location = useLocation()
  const [status, setStatus] = useState<TrialUsageStatus | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!user) {
      setStatus(null)
      return
    }

    api.stripe
      .getTrialUsageStatus()
      .then((nextStatus) => {
        if (!cancelled) setStatus(nextStatus)
      })
      .catch(() => {
        if (!cancelled) setStatus(null)
      })

    return () => {
      cancelled = true
    }
  }, [user, location.pathname])

  const message = useMemo(
    () => deadlineMessage(status?.free_plan_end_date),
    [status?.free_plan_end_date]
  )

  if (!user || status?.access_status !== 'legacy_free_active') {
    return null
  }

  return (
    <div className="sticky top-[calc(var(--native-safe-area-top)+5.75rem)] z-40 mt-[calc(var(--native-safe-area-top)+5.75rem)] border-b border-amber-200 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-50">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 text-sm font-medium">
          <Clock className="mt-0.5 h-4 w-4 flex-none" />
          <span>{message}</span>
        </div>
        <Link
          to="/pricing"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-amber-400 dark:text-slate-950 dark:hover:bg-amber-300"
        >
          Upgrade to Premium
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
